import _ from "lodash";
import { getEnabledElement } from "component/toolManager/enabledElements.js";
import calculateSUV from "component/util/calculateSUV";
import makeKey from "core/cache/KeyGen.js";
import Cache from "core/cache";
import Color from "component/molecules/viewport/core/color/";
import { intersection } from "lodash";
import {
  resetCanvasContextTransform,
  transformCanvasContext,
} from "component/molecules/viewport/draw/index.js";
import math from "core/math";

if (!("createImageBitmap" in window)) {
  window.createImageBitmap = async function (imageData) {
    return new Promise((resolve) => {
      const img = document.createElement("img");

      img.addEventListener("load", function () {
        resolve(this);
      });

      const conversionCanvas = document.createElement("canvas");

      conversionCanvas.width = imageData.width;
      conversionCanvas.height = imageData.height;

      const conversionCanvasContext = conversionCanvas.getContext("2d");

      conversionCanvasContext.putImageData(
        imageData,
        0,
        0,
        0,
        0,
        conversionCanvas.width,
        conversionCanvas.height
      );
      img.src = conversionCanvas.toDataURL();
    });
  };
}

function generateLinearModalityLUT(slope, intercept) {
  return (storedPixelValue) => storedPixelValue * slope + intercept;
}

/**
 *
 * @param {Number} windowWidth Window Width
 * @param {Number} windowCenter Window Center
 * @returns {VOILUTFunction} VOI LUT mapping function
 * @memberof VOILUT
 */
function generateLinearVOILUT(windowWidth, windowCenter) {
  return function (modalityLutValue) {
    return ((modalityLutValue - windowCenter) / windowWidth + 0.5) * 255.0;
  };
}

const generateNormalLUT = (
  SUVbwLut,
  image,
  windowWidth,
  windowCenter,
  invert,
  modalityLUT,
  voiLUT
) => {
  const maxPixelValue = Math.max(image.minMax[0], image.minMax[1]);
  const minPixelValue = Math.min(image.minMax[0], image.minMax[1]);
  const offset = Math.min(minPixelValue, 0);

  if (image.cachedLut === undefined) {
    const length = maxPixelValue - offset + 1;

    image.cachedLut = {};
    image.cachedLut.lutArray = new Uint8ClampedArray(length);
  }

  const mlutfn = generateLinearModalityLUT(
    image.rescaleSlope,
    image.rescaleIntercept,
    modalityLUT
  );
  const vlutfn = generateLinearVOILUT(windowWidth, windowCenter, voiLUT);

  if (invert === true) {
    for (
      let storedValue = minPixelValue;
      storedValue <= maxPixelValue;
      storedValue++
    ) {
      SUVbwLut[storedValue + -offset] = 255 - vlutfn(mlutfn(storedValue));
      //lut[storedValue + -offset] = 100;
    }
  } else {
    for (
      let storedValue = minPixelValue;
      storedValue <= maxPixelValue;
      storedValue++
    ) {
      SUVbwLut[storedValue + -offset] = vlutfn(mlutfn(storedValue));
      //lut[storedValue + -offset] = 100;
    }
  }
  return SUVbwLut;
};

const generateSUVBasedLUT = (
  SUVbwLut,
  image,
  windowWidth,
  windowCenter,
  invert,
  modalityLUT,
  voiLUT
) => {
  const maxPixelValue = Math.max(image.minMax[0], image.minMax[1]);
  const minPixelValue = Math.min(image.minMax[0], image.minMax[1]);
  const offset = Math.min(minPixelValue, 0);

  const mlutfn = generateLinearModalityLUT(
    image.rescaleSlope,
    image.rescaleIntercept,
    modalityLUT
  );
  const vlutfn = generateLinearVOILUT(windowWidth, windowCenter, voiLUT);
  const maxInVOI = windowCenter + windowWidth / 2;
  const minInVOI = windowCenter - windowWidth / 2;
  const minSUV = calculateSUV(image, minInVOI);
  const maxSUV = calculateSUV(image, maxInVOI);

  for (
    let storedValue = minPixelValue;
    storedValue <= maxPixelValue;
    storedValue++
  ) {
    //SUVbwLut[storedValue + -offset] = vlutfn(mlutfn(storedValue));
    // VOI 범위 밖 픽셀 값 필터링
    const unitValueInVOI = vlutfn(mlutfn(storedValue));
    if (unitValueInVOI >= 255) {
      SUVbwLut[storedValue + -offset] = 255;
      continue;
    } else if (unitValueInVOI <= 0) {
      SUVbwLut[storedValue + -offset] = 0;
      continue;
    }

    let suv = calculateSUV(image, storedValue);
    let normalizedSUV = ((suv - minSUV) / maxSUV) * 255;
    SUVbwLut[storedValue + -offset] = normalizedSUV;
  }

  return SUVbwLut;
};
const cachedImage = null;
const renderOverlayImage = (
  context,
  eventData,
  targetElement,
  targetImageIdx,
  referenceElement
) => {
  const targetEnabledElement = getEnabledElement(targetElement);
  const referenceEnabledElement = getEnabledElement(referenceElement);
  const targetImageRef = getEnabledElement(targetElement).image;
  const targetSeriesKey = makeKey(
    targetImageRef.seriesInstanceUID,
    targetImageRef.modality
  );
  /** 2. targetElement에서 sourceElement의 현재 프레임과 대응되는 Frame을 검색 */
  let metadataCache = new Cache.Metadata();
  const targetImage = metadataCache.get(targetSeriesKey).stack[0].frame[
    targetImageIdx
  ];
  const referenceImage = getEnabledElement(referenceElement).image;

  // Make sure the images are actually loaded for the target and reference
  if (!targetImage || !referenceImage) {
    return;
  }

  // Referc
  const referenceSeriesKey = makeKey(
    referenceImage.seriesInstanceUID,
    referenceImage.modality
  );
  if (!!referenceEnabledElement.fusionCache === false) {
    // Overlay 이미지 생성
    const imageData = context.createImageData(
      parseInt(referenceImage.rows),
      parseInt(referenceImage.columns)
    ); /// create a canvas buffer (RGBA)
    var data = imageData.data; /// view for the canvas buffer
    var len = data.length;

    // SUVbw 값 image를 Colormap에 매핑한 LUT 획득
    const targetViewport = targetEnabledElement.viewport;
    const colormapBuilder = Color.getColormap("hot");
    const hotIronColormap = colormapBuilder.getColormap();
    const SUVbwLUT = new Uint8ClampedArray(
      targetImage.columns * targetImage.rows
    );
    const targetModality = targetImage.modality;
    if (targetModality.toLowerCase() === "pt") {
      generateSUVBasedLUT(
        SUVbwLUT,
        targetImage,
        targetViewport.voi.windowWidth,
        targetViewport.voi.windowCenter,
        targetViewport.invert,
        targetViewport.modalityLUT,
        targetViewport.voiLUT
      );
    } else {
      generateNormalLUT(
        SUVbwLUT,
        targetImage,
        targetViewport.voi.windowWidth,
        targetViewport.voi.windowCenter,
        targetViewport.invert,
        targetViewport.modalityLUT,
        targetViewport.voiLUT
      );
    }

    for (let i = 0; i < len; i += 4) {
      let refX = (i / 4) % referenceImage.columns;
      let refY = i / 4 / referenceImage.rows;
      let targetX = parseInt(
        (refX / referenceImage.columns) * targetImage.columns
      );
      let targetY = parseInt((refY / referenceImage.rows) * targetImage.rows);
      let targetIdx = targetY * targetImage.rows + targetX;
      data[i] = hotIronColormap[SUVbwLUT[targetImage.pixelData[targetIdx]]][0];
      data[i + 1] =
        hotIronColormap[SUVbwLUT[targetImage.pixelData[targetIdx]]][1];
      data[i + 2] =
        hotIronColormap[SUVbwLUT[targetImage.pixelData[targetIdx]]][2];
      data[i + 3] = 64;
    }

    window.createImageBitmap(imageData).then(function (newImageBitmap) {
      referenceEnabledElement.fusionCache = newImageBitmap;
      const canvasTopLeft = math.coordinate.pixelToCanvas(
        referenceEnabledElement.element,
        {
          x: 0,
          y: 0,
        }
      );
      //const alpha = targetImage.pixelSpacing[0] / ((1.52) * referenceImage.pixelSpacing[0]);
      const alpha =
        targetImage.pixelSpacing[0] /
        ((referenceImage.rows / targetImage.rows) *
          referenceImage.pixelSpacing[0]);
      const canvasTopRight = math.coordinate.pixelToCanvas(
        referenceEnabledElement.element,
        {
          x: referenceImage.columns * alpha,
          y: 0,
        }
      );

      const canvasBottomRight = math.coordinate.pixelToCanvas(
        referenceEnabledElement.element,
        {
          x: referenceImage.columns * alpha,
          y: referenceImage.rows * alpha,
        }
      );
      const canvas = referenceEnabledElement.canvas;
      const viewport = referenceEnabledElement.viewport;
      const CanvasWidth = math.point.distance(canvasTopLeft, canvasTopRight);
      const CanvasHeight = math.point.distance(
        canvasTopRight,
        canvasBottomRight
      );
      resetCanvasContextTransform(context);
      transformCanvasContext(context, canvas, viewport);
      const canvasViewportTranslation = {
        x: viewport.translation.x * viewport.scale,
        y: viewport.translation.y * viewport.scale,
      };

      context.drawImage(
        referenceEnabledElement.fusionCache,
        canvas.width / 2 - CanvasWidth / 2 + canvasViewportTranslation.x,
        canvas.height / 2 - CanvasHeight / 2 + canvasViewportTranslation.y,
        CanvasWidth,
        CanvasHeight
      );

      context.globalAlpha = 1.0;

      resetCanvasContextTransform(context);
    });
    return;
  }
};

export default renderOverlayImage;
